import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatCard } from "@/components/admin/StatCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingUp, Activity, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [running, setRunning] = useState(false);

  const loadHealth = () => api.get("/admin/system-health").then(({ data }) => setHealth(data)).catch(() => {});
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setData(data)).catch(() => {}); loadHealth(); }, []);

  const runMaintenance = async () => {
    setRunning(true);
    try { await api.post("/admin/maintenance/run"); toast.success("Maintenance started"); setTimeout(loadHealth, 2000); }
    catch { toast.error("Could not start maintenance"); }
    setRunning(false);
  };

  if (!data) return <p className="text-muted-foreground">Loading analytics…</p>;

  const u = data.users, s = data.submissions;
  const maxTrend = Math.max(1, ...data.signups_trend.map((t) => t.count));
  const hOk = health && (health.status === "ok");
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

  return (
    <div data-testid="admin-overview">
      <h1 className="font-serif text-4xl tracking-tight mb-1">Overview</h1>
      <p className="text-muted-foreground mb-8">Live snapshot of your platform’s activity.</p>

      {/* System health */}
      <div className="grid-card p-6 mb-6" data-testid="system-health">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={`grid place-items-center w-10 h-10 shrink-0 ${hOk ? "bg-emerald-600" : health && health.status !== "never" ? "bg-amber-500" : "bg-slate-400"} text-white`}>
              {hOk ? <CheckCircle2 size={20} /> : <Activity size={20} />}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-serif text-xl">System health</h2>
                {health && health.status !== "never" && (
                  <Badge className={`rounded-sm ${hOk ? "bg-emerald-600" : "bg-amber-500"}`}>{health.status.toUpperCase()}</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Automated maintenance runs daily at 3:00 AM ET · last run {fmt(health?.last_run)}
                {health?.db_ok !== undefined && <> · DB {health.db_ok ? "reachable" : "unreachable"}</>}
                {typeof health?.trials_expired === "number" && <> · {health.trials_expired} expired trials cleaned</>}
              </p>
            </div>
          </div>
          <Button onClick={runMaintenance} disabled={running} data-testid="run-maintenance-btn" variant="outline" className="rounded-sm gap-2 hover:bg-secondary">
            <RefreshCw size={15} className={running ? "animate-spin" : ""} /> {running ? "Running…" : "Run now"}
          </Button>
        </div>
        {health?.status === "error" && (
          <p className="text-xs text-red-600 mt-3 flex items-center gap-1.5"><AlertTriangle size={13} /> {health.error}</p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-total-users" label="Total users" value={u.total} sub={`+${u.new_30d} in last 30 days`} />
        <StatCard testid="stat-active-subs" label="Active subscribers" value={u.active} accent sub={`${u.trialing} on trial`} />
        <StatCard testid="stat-comped" label="Comped accounts" value={u.comped} sub={`${u.admins} admin${u.admins === 1 ? "" : "s"}`} />
        <StatCard testid="stat-submissions" label="Submissions" value={s.total} sub={`${s.filed} filed · ${s.not_filed} in progress`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Signups trend */}
        <div className="grid-card p-6" data-testid="signups-chart">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp size={16} className="text-accent" />
            <h2 className="font-serif text-xl">New sign-ups · 14 days</h2>
          </div>
          <div className="flex items-end gap-1.5 h-40">
            {data.signups_trend.map((t) => (
              <div key={t.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="w-full bg-primary/15 rounded-t-sm relative" style={{ height: `${(t.count / maxTrend) * 100}%`, minHeight: t.count ? "6px" : "2px" }}>
                  <div className="absolute inset-0 bg-accent rounded-t-sm opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <span className="text-[0.6rem] text-muted-foreground">{t.date.slice(8)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top forms */}
        <div className="grid-card p-6" data-testid="top-forms">
          <h2 className="font-serif text-xl mb-6">Most-used forms</h2>
          {data.top_forms.length === 0 ? (
            <p className="text-sm text-muted-foreground">No submissions yet.</p>
          ) : (
            <div className="space-y-3">
              {data.top_forms.map((f) => (
                <div key={f.number} className="flex items-center gap-3">
                  <Badge variant="outline" className="rounded-sm font-mono text-xs shrink-0">{f.number}</Badge>
                  <span className="text-sm truncate flex-1">{f.title}</span>
                  <span className="text-sm font-semibold tabular-nums">{f.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
